
import { useState, useEffect, Dispatch, SetStateAction } from 'react';

export interface NavigableDocument {
    id: string;
}

interface UseDocumentNavigationReturn<T extends NavigableDocument> {
    isDrawerOpen: boolean;
    selectedDocument: T | null;
    currentIndex: number;
    setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
    setSelectedDocument: Dispatch<SetStateAction<T | null>>;
    handleNext: () => void;
    handlePrevious: () => void;
    closeDrawer: () => void;
    openDrawer: (doc: T) => void;
}

export function useDocumentNavigation<T extends NavigableDocument>(
    documents: T[]
): UseDocumentNavigationReturn<T> {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<T | null>(null);

    const currentIndex = selectedDocument
        ? documents.findIndex(doc => doc.id === selectedDocument.id)
        : -1;

    const handleNext = () => {
            if (currentIndex === -1 || currentIndex >= documents.length - 1) return;
            setSelectedDocument(documents[currentIndex + 1]);
        };

        const handlePrevious = () => {
            if (currentIndex <= 0) return;
            setSelectedDocument(documents[currentIndex - 1]);
        };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedDocument(null);
    };

    const openDrawer = (doc: T) => {
        setSelectedDocument(doc);
        setIsDrawerOpen(true);
    };

    useEffect(() => {
        if (!selectedDocument) {
            return;
        }
        const stillExists = documents.some((doc) => doc.id === selectedDocument.id);
        if (!stillExists) {
            setIsDrawerOpen(false);
            setSelectedDocument(null);
        }
    }, [documents, selectedDocument]);

    return {
        isDrawerOpen,
        selectedDocument,
        currentIndex,
        setIsDrawerOpen,
        setSelectedDocument,
        handleNext,
        handlePrevious,
        closeDrawer,
        openDrawer
    };
}